/**
 * Raid environment: time of day and weather. Decided ONCE per raid on the
 * server from the seed (`ENV_RNG_SELECTOR`) and sent in `welcome.env`. The
 * client PAINTS it, the server RULES by it (scav sight and hearing); both read
 * the numbers from here. Pure data and functions.
 */

import type { Rng } from "../math/rng.js";

export type TimeOfDay = "dawn" | "day" | "dusk" | "night";
export type Weather = "clear" | "fog" | "rain" | "snow";

export interface RaidEnv {
  time: TimeOfDay;
  weather: Weather;
}

/** Wire/iteration order. Append-only. */
export const TIMES_OF_DAY: readonly TimeOfDay[] = ["dawn", "day", "dusk", "night"];
export const WEATHERS: readonly Weather[] = ["clear", "fog", "rain", "snow"];

/** The default when nothing says otherwise: a legacy server, the menu preview. */
export const DEFAULT_ENV: RaidEnv = { time: "day", weather: "clear" };

/**
 * Spawn weights. Day is the common case; night is frequent enough that the lamp
 * earns its slot.
 */
const TIME_WEIGHTS: readonly [TimeOfDay, number][] = [
  ["day", 45],
  ["dawn", 15],
  ["dusk", 15],
  ["night", 25],
];
/**
 * Snow took its weight out of all three, not rain alone — a Tirolean valley is
 * white a third of the year, and footprints have to come round often enough to
 * be learned. The env stream is its own rng selector, so nothing downstream
 * moves.
 */
const WEATHER_WEIGHTS: readonly [Weather, number][] = [
  ["clear", 48],
  ["fog", 20],
  ["rain", 18],
  ["snow", 14],
];

function weighted<T>(rng: Rng, table: readonly [T, number][]): T {
  let total = 0;
  for (const [, w] of table) total += w;
  let r = rng.next() * total;
  for (const [v, w] of table) {
    r -= w;
    if (r < 0) return v;
  }
  return table[table.length - 1]![0];
}

/** Draws exactly two numbers from `rng`, in this order: time, then weather. */
export function pickEnv(rng: Rng): RaidEnv {
  const time = weighted(rng, TIME_WEIGHTS);
  const weather = weighted(rng, WEATHER_WEIGHTS);
  return { time, weather };
}

/**
 * How much of a scav's daylight sight range survives this environment.
 * Multiplied into `SIGHT_RANGE_M`; time and weather compound.
 */
export function lightFactor(env: RaidEnv): number {
  let f: number;
  switch (env.time) {
    case "day":
      f = 1;
      break;
    case "dawn":
    case "dusk":
      f = 0.7;
      break;
    case "night":
      f = 0.35;
      break;
  }
  if (env.weather === "fog") f *= 0.6;
  else if (env.weather === "rain") f *= 0.85;
  // Falling snow sits between rain and fog: flat white sky, but bright ground.
  else if (env.weather === "snow") f *= 0.75;
  return f;
}

/**
 * How much of a scav's hearing radius survives. Rain drowns footsteps; snow
 * MUFFLES them, more gently — what you cannot be heard doing in snow, you can
 * be tracked doing.
 */
export function hearingFactor(env: RaidEnv): number {
  if (env.weather === "rain") return 0.6;
  if (env.weather === "snow") return 0.7;
  return 1;
}

/** Wire guard for `welcome.env`. */
export function isRaidEnv(x: unknown): x is RaidEnv {
  if (typeof x !== "object" || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    (TIMES_OF_DAY as readonly unknown[]).includes(o["time"]) &&
    (WEATHERS as readonly unknown[]).includes(o["weather"])
  );
}

/** Parses `ENV=night,rain` style overrides; `null` when unparseable. */
export function parseEnv(s: string | undefined): RaidEnv | null {
  if (!s) return null;
  const parts = s.split(",").map((p) => p.trim().toLowerCase());
  const time = parts.find((p) => (TIMES_OF_DAY as readonly string[]).includes(p)) as
    | TimeOfDay
    | undefined;
  const weather = parts.find((p) => (WEATHERS as readonly string[]).includes(p)) as
    | Weather
    | undefined;
  if (time === undefined && weather === undefined) return null;
  return { time: time ?? DEFAULT_ENV.time, weather: weather ?? DEFAULT_ENV.weather };
}

/** Human label, German like the rest of the UI: "Nacht · Regen". */
export function envLabel(env: RaidEnv): string {
  const t: Record<TimeOfDay, string> = {
    dawn: "Morgengrauen",
    day: "Tag",
    dusk: "Dämmerung",
    night: "Nacht",
  };
  const w: Record<Weather, string> = { clear: "Klar", fog: "Nebel", rain: "Regen", snow: "Schnee" };
  return `${t[env.time]} · ${w[env.weather]}`;
}
